import { useState } from 'react';
import { BellRing } from 'lucide-react';
import {
  formatAttendanceBreakdown,
  type CampaignQuestionResult,
  type InviteCampaignStats,
} from '@peles/shared';
import { useDialog } from '../../../context/DialogContext';
import { useCan } from '../../../hooks/useCan';
import {
  useCampaignRecipientPage,
  useInviteCampaignStats,
  useRemindInviteCampaign,
} from '../../../hooks/useInvites';
import type { CampaignStepProps } from './CampaignDetailsStep';

const NON_RESPONDERS_SHOWN = 50;
/** The invitations a reminder can follow — the ones that arrived. */
const REMINDABLE = new Set(['SENT', 'DELIVERED', 'OPENED']);
const card = 'rounded-2xl border border-gray-200 p-4';

/** Sent → Opened → Answered → Coming, each bar a share of the one before. */
export function CampaignFunnel({ stats }: { stats: InviteCampaignStats }) {
  const answered = stats.yes + stats.no + stats.maybe;
  const steps = [
    { label: 'Sent', value: stats.sent },
    { label: 'Opened', value: stats.opened },
    { label: 'Answered', value: answered },
    { label: 'Coming', value: stats.yes },
  ];
  const top = Math.max(stats.sent, answered, 1);
  return (
    <ol className="space-y-2" aria-label="Campaign funnel">
      {steps.map((step, index) => {
        const previous = index === 0 ? null : steps[index - 1].value;
        const share = previous ? Math.round((step.value / previous) * 100) : null;
        return (
          <li
            key={step.label}
            className="grid grid-cols-[5.5rem_minmax(0,1fr)_5rem] items-center gap-3"
          >
            <span className="text-sm text-gray-700">{step.label}</span>
            <span className="h-3 overflow-hidden rounded-full bg-gray-100">
              <span
                className="block h-full rounded-full bg-primary-600"
                style={{ width: `${Math.min(100, (step.value / top) * 100)}%` }}
              />
            </span>
            <span className="text-right text-sm">
              <span className="font-semibold text-gray-900">{step.value}</span>
              {share !== null && <span className="ml-1 text-xs text-gray-500">{share}%</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function QuestionResult({ result }: { result: CampaignQuestionResult }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-900">{result.prompt}</p>
      <p className="text-xs text-gray-500">
        {result.answered} answer{result.answered === 1 ? '' : 's'}
      </p>
      {result.choices && (
        <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
          {result.choices.map((choice) => (
            <li key={choice.label} className="flex justify-between gap-3">
              <span>{choice.label}</span>
              <span className="font-medium">{choice.count}</span>
            </li>
          ))}
        </ul>
      )}
      {result.yes !== undefined && (
        <p className="mt-1 text-sm text-gray-700">
          Yes {result.yes} · No {result.no}
        </p>
      )}
      {result.total !== undefined && (
        <p className="mt-1 text-sm text-gray-700">
          Total {result.total} · Average {result.average}
        </p>
      )}
      {result.texts && result.texts.length > 0 && (
        <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto text-sm text-gray-700">
          {result.texts.map((text, index) => (
            <li key={index}>
              <span className="text-gray-500">{text.guestName}:</span> {text.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Step 6: how it went — the funnel, the head count and meals, the answers to
 * the campaign's questions, and who has not answered, to remind.
 */
export function CampaignResultsStep({ campaign }: CampaignStepProps) {
  const { alert, confirm } = useDialog();
  const canSend = useCan('messages.guests');
  const { data: stats, isLoading } = useInviteCampaignStats(campaign.id);
  const { data: waiting } = useCampaignRecipientPage(campaign.id, {
    page: 1,
    pageSize: NON_RESPONDERS_SHOWN,
    // Everyone invited who has not answered — a bounced or failed invitation
    // included, since that guest has not answered either.
    groups: ['sent', 'opened', 'bounced', 'failed'],
    responses: ['PENDING'],
  });
  const remind = useRemindInviteCampaign();
  const [picked, setPicked] = useState<string[]>([]);

  const handleRemind = async (recipientIds?: string[]) => {
    const who = recipientIds
      ? `the ${recipientIds.length} guest${recipientIds.length === 1 ? '' : 's'} picked`
      : 'everyone the invitation reached who has not answered';
    const ok = await confirm({ message: `Send a reminder to ${who}?`, confirmLabel: 'Remind' });
    if (!ok) return;
    try {
      const result = await remind.mutateAsync({ campaignId: campaign.id, recipientIds });
      setPicked([]);
      await alert({
        message: `Reminded ${result.reminded} guest${result.reminded === 1 ? '' : 's'}.`,
        variant: 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not send the reminders',
        variant: 'error',
      });
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading the results…</p>;
  if (!stats) return <p className="text-sm text-gray-500">No results yet.</p>;

  // Who a reminder would reach now, as the server counts it: invitations that
  // arrived, to guests who have not answered by any route.
  const remindable = waiting?.remindable ?? 0;
  const breakdown = formatAttendanceBreakdown(stats.attendingAdults, stats.attendingKids);
  const pending = waiting?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={card}>
          <h3 className="mb-3 text-sm font-semibold text-gray-900">How it went</h3>
          <CampaignFunnel stats={stats} />
          {stats.bounced > 0 && (
            <p className="mt-2 text-xs text-red-600">
              {stats.bounced} invitation{stats.bounced === 1 ? '' : 's'} bounced.
            </p>
          )}
        </section>
        <section className={card}>
          <h3 className="text-sm font-semibold text-gray-900">Coming</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {stats.attendingAdults + stats.attendingKids}
          </p>
          <p className="text-sm text-gray-600">{breakdown || 'Nobody yet'}</p>
          <p className="mt-2 text-xs text-gray-500">
            {stats.yes} yes · {stats.maybe} maybe · {stats.no} no · {stats.pending} waiting
          </p>
          {stats.perEvent.length > 1 && (
            <ul className="mt-3 space-y-0.5 text-xs text-gray-600">
              {stats.perEvent.slice(1).map((e) => (
                <li key={e.eventId} className="flex justify-between gap-2">
                  <span className="truncate">{e.eventName}</span>
                  <span className="shrink-0">
                    {e.attendingAdults + e.attendingKids} coming · {e.yes} yes
                  </span>
                </li>
              ))}
            </ul>
          )}
          {stats.mealCounts.length > 0 && (
            <div className="mt-3 text-xs text-gray-600">
              <span className="font-medium">Meals: </span>
              {stats.mealCounts.map((meal) => `${meal.name} × ${meal.count}`).join(' · ')}
              {stats.unassignedMeals > 0 && ` · ${stats.unassignedMeals} not chosen`}
            </div>
          )}
        </section>
      </div>

      {(stats.questions?.length ?? 0) > 0 && (
        <section className={`${card} space-y-4`}>
          <h3 className="text-sm font-semibold text-gray-900">Answers to your questions</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {stats.questions!.map((result) => (
              <QuestionResult key={result.questionId} result={result} />
            ))}
          </div>
        </section>
      )}

      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Not answered yet{waiting ? ` (${waiting.total})` : ''}
          </h3>
          {canSend && campaign.status === 'SENT' && remindable > 0 && (
            <div className="flex gap-2">
              {picked.length > 0 && (
                <button
                  type="button"
                  onClick={() => void handleRemind(picked)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <BellRing className="h-4 w-4" aria-hidden />
                  Remind {picked.length} picked
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleRemind()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700"
              >
                <BellRing className="h-4 w-4" aria-hidden />
                Remind everyone reached
              </button>
            </div>
          )}
        </div>
        {pending.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Everybody invited has answered.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {pending.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2">
                {canSend && (
                  <input
                    type="checkbox"
                    aria-label={`Pick ${r.guestName ?? 'guest'}`}
                    // A reminder follows the invitation: one that never
                    // arrived is sent again from the Guests step instead.
                    disabled={!REMINDABLE.has(r.status)}
                    checked={picked.includes(r.id)}
                    onChange={(e) =>
                      setPicked((current) =>
                        e.target.checked ? [...current, r.id] : current.filter((id) => id !== r.id),
                      )
                    }
                  />
                )}
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                  {r.guestName ?? 'Unnamed guest'}
                </span>
                <span
                  className={`text-xs ${REMINDABLE.has(r.status) ? 'text-gray-500' : 'text-red-600'}`}
                >
                  {r.status === 'BOUNCED'
                    ? 'Bounced — send it again or share their link'
                    : r.status === 'FAILED'
                      ? 'Could not be sent'
                      : r.remindersSent
                        ? `Reminded ${r.remindersSent}×`
                        : r.status === 'OPENED'
                          ? 'Opened'
                          : 'Not opened'}
                </span>
              </li>
            ))}
            {(waiting?.total ?? 0) > pending.length && (
              <li className="py-2 text-xs text-gray-500">
                and {(waiting?.total ?? 0) - pending.length} more
              </li>
            )}
          </ul>
        )}
      </section>
    </div>
  );
}
